import { withMetadata } from '@pactswap/cwap-cm-lib/history/offchain';
import { HISTORY_ACCESS_FEE } from '@pactswap/cwap-cm-lib/history/shared';
import { constructCall, constructUiCommand } from '@pactswap/cwap-cm-lib/offchain';
import { toHex32 } from '@pactswap/cwap-cm-lib/shared';
import { FEE, PUBLIC_METHODS } from './shared';
import { ordersBatchToList, serializeOrdersBatch } from './shared/utils';
/**
 * Constructs a deposit call object and might be used to create a UI-command
 * with QR code for wallet scanning.
 *
 * @param data - Deposit request parameters
 * @param data.contractId - The L2 contract address for the market maker contract
 * @param data.depositAmount - The amount of CWEB to deposit (in smallest unit)
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A contract call object for depositing CWEB collateral
 *
 * @example
 * ```typescript
 * const depositCall = constructMakeDepositCall({
 *   contractId: '0x...',
 *   depositAmount: 1000000000000000000n, // 1 CWEB
 *   accessId: 'unique-tracking-id'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={constructUiCommand([depositCall])} />
 * ```
 */
export const constructMakeDepositCall = ({ contractId, depositAmount, accessId }) => {
    return constructCall(contractId, PUBLIC_METHODS.DEPOSIT, withMetadata([toHex32(depositAmount)], accessId), FEE.DEPOSIT + depositAmount + (accessId ? HISTORY_ACCESS_FEE : 0n));
};
/**
 * Constructs a withdraw call object and might be used to create a UI-command
 * with QR code for wallet scanning.
 *
 * @param data - Withdraw request parameters
 * @param data.contractId - The L2 contract address for the market maker contract
 * @param data.withdrawAmount - The amount of CWEB to withdraw (in smallest unit)
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A contract call object for withdrawing CWEB collateral
 *
 * @example
 * ```typescript
 * const withdrawCall = constructMakeWithdrawCall({
 *   contractId: '0x...',
 *   withdrawAmount: 500000000000000000n, // 0.5 CWEB
 *   accessId: 'unique-tracking-id'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={constructUiCommand([withdrawCall])} />
 * ```
 */
export const constructMakeWithdrawCall = ({ contractId, withdrawAmount, accessId }) => {
    return constructCall(contractId, PUBLIC_METHODS.WITHDRAW, withMetadata([toHex32(withdrawAmount)], accessId), FEE.WITHDRAW + (accessId ? HISTORY_ACCESS_FEE : 0n));
};
/**
 * Internal helper function that constructs a contract call object for creating
 * one or more orders and might be used to create a UI-command with QR code for wallet scanning.
 *
 * @param data - Create orders request parameters
 * @param data.contractId - The L2 contract address for the market maker contract
 * @param data.orders - Batch of orders to create. Format: `[count, baseOrder, tunes?][]`
 *   - `count`: Number of orders to create with the base template
 *   - `baseOrder`: Base order template with `baseAmount`, `l1Amount`, `baseRecipient`, `minL1Amount`
 *   - `tunes`: Optional array of order-specific overrides for each order in the batch
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A contract call object ready to be included in a UI command
 * @throws {Error} If the total l1Amount is less than the total minL1Amount
 *
 * @example
 * ```typescript
 * // Create a single order
 * const createOrderCall = constructCreateOrdersCall({
 *   contractId: '0x...',
 *   orders: [
 *     [
 *       1, // Create 1 order
 *       {
 *         baseAmount: 1000000000000000000n, // 1 CWEB
 *         l1Amount: 50000000n, // 0.5 BTC (in satoshis)
 *         minL1Amount: 40000000n, // 0.4 BTC minimum
 *         baseRecipient: {
 *           auth: 'EcdsaContract',
 *           payload: 'k:user-address'
 *         }
 *       }
 *     ]
 *   ],
 *   accessId: 'unique-tracking-id'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={constructUiCommand([createOrderCall])} />
 *
 * // Create multiple orders with variations
 * const createMultipleOrdersCall = constructCreateOrdersCall({
 *   contractId: '0x...',
 *   orders: [
 *     [
 *       3, // Create 3 orders
 *       {
 *         baseAmount: 1000000000000000000n, // Base: 1 CWEB
 *         l1Amount: 50000000n, // Base: 0.5 BTC
 *         baseRecipient: { auth: 'EcdsaContract', payload: 'k:user' }
 *       },
 *       [
 *         { baseAmount: 1100000000000000000n }, // Order 1: 1.1 CWEB
 *         { l1Amount: 60000000n }, // Order 2: 0.6 BTC
 *         { baseAmount: 1200000000000000000n, l1Amount: 70000000n } // Order 3: both changed
 *       ]
 *     ]
 *   ]
 * });
 * ```
 */
export const constructCreateOrdersCall = ({ contractId, orders, accessId }) => {
    const ordersList = ordersBatchToList(orders);
    const { l1Amount, minL1Amount } = ordersList.reduce((result, order) => ({
        l1Amount: result.l1Amount + BigInt(order.l1Amount ?? 0n),
        minL1Amount: result.minL1Amount + BigInt(order.minL1Amount ?? 0n),
    }), { l1Amount: 0n, minL1Amount: 0n });
    if (l1Amount < minL1Amount) {
        throw new Error('Amount is less than minimum value');
    }
    return constructCall(contractId, PUBLIC_METHODS.CREATE_ORDER, withMetadata([serializeOrdersBatch(orders)], accessId), FEE.CREATE_ORDER * BigInt(ordersList.length) + (accessId ? HISTORY_ACCESS_FEE : 0n));
};
/**
 * Constructs a cancel orders call object and might be used to create a UI-command
 * with QR code for wallet scanning.
 *
 * @param data - Cancel orders request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.orderIds - Array of order IDs to cancel
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A contract call object ready to be included in a UI command
 *
 * @example
 * ```typescript
 * // Cancel a single order
 * const cancelOrderCall = constructCancelOrdersCall({
 *   contractId: '0x...',
 *   orderIds: ['order-id-123'],
 *   accessId: 'unique-tracking-id'
 * });
 *
 * // Cancel multiple orders
 * const cancelMultipleOrdersCall = constructCancelOrdersCall({
 *   contractId: '0x...',
 *   orderIds: ['order-id-1', 'order-id-2', 'order-id-3']
 * });
 * ```
 */
export const constructCancelOrdersCall = ({ contractId, orderIds, accessId }) => {
    return constructCall(contractId, PUBLIC_METHODS.CANCEL_ORDER, withMetadata([orderIds], accessId), FEE.CANCEL_ORDER * BigInt(orderIds.length) + (accessId ? HISTORY_ACCESS_FEE : 0n));
};
/**
 * Creates a UI command for depositing CWEB collateral.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the deposit transaction.
 *
 * @param data - Deposit request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.depositAmount - The amount of CWEB to deposit (in smallest unit, e.g., wei)
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Basic usage
 * const depositCommand = makeDepositUiCommand({
 *   contractId: '0x...',
 *   depositAmount: 10000000000000000000n, // 10 CWEB
 *   accessId: 'tracking-id-123' // Optional tracking
 * });
 *
 * // Use in QRCode component
 * <QRCode value={depositCommand} />
 * ```
 */
export const makeDepositUiCommand = (data) => {
    return constructUiCommand([constructMakeDepositCall(data)]);
};
/**
 * Creates a UI command for withdrawing CWEB collateral.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the withdraw transaction.
 *
 * @param data - Withdraw request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.withdrawAmount - The amount of CWEB to withdraw (in smallest unit, e.g., wei)
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Basic usage
 * const withdrawCommand = makeWithdrawUiCommand({
 *   contractId: '0x...',
 *   withdrawAmount: 5000000000000000000n, // 5 CWEB
 *   accessId: 'tracking-id-123' // Optional tracking
 * });
 *
 * // Use in QRCode component
 * <QRCode value={withdrawCommand} />
 * ```
 */
export const makeWithdrawUiCommand = (data) => {
    return constructUiCommand([constructMakeWithdrawCall(data)]);
};
/**
 * Creates a UI command for creating a single order.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the create order transaction.
 *
 * @param data - Create order request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.baseAmount - The amount of base currency (CWEB) to receive (in smallest unit)
 * @param data.l1Amount - The amount of L1 token to sell (in smallest unit, e.g., satoshis for BTC)
 * @param data.baseRecipient - The recipient address for the base currency (CWEB)
 * @param data.minL1Amount - Optional minimum L1 amount to accept (defaults to 0n)
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Create a sell order: sell 0.5 BTC for 1 CWEB
 * const createOrderCommand = createOrderUiCommand({
 *   contractId: '0x...',
 *   baseAmount: 1000000000000000000n, // 1 CWEB
 *   l1Amount: 50000000n, // 0.5 BTC (in satoshis)
 *   minL1Amount: 40000000n,
 *   baseRecipient: {
 *     auth: 'EcdsaContract',
 *     payload: 'k:user-address'
 *   },
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={createOrderCommand} />
 * ```
 */
export const createOrderUiCommand = ({ contractId, baseRecipient, l1Amount, minL1Amount = 0n, baseAmount, accessId, }) => {
    return constructUiCommand([
        constructCreateOrdersCall({
            contractId,
            orders: [
                [
                    1,
                    {
                        baseAmount,
                        l1Amount,
                        baseRecipient,
                        minL1Amount,
                    },
                ],
            ],
            accessId,
        }),
    ]);
};
/**
 * Creates a UI command for creating a batch of one or more orders.
 * It supports creating multiple orders in a single transaction, which is more efficient
 * than creating them individually.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the create orders transaction.
 *
 * @param data - Create orders request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.orders - Batch of orders to create. Format: `[count, baseOrder, tunes?][]`
 *   - `count`: Number of orders to create with the base template
 *   - `baseOrder`: Base order template with `baseAmount`, `l1Amount`, `baseRecipient`, `minL1Amount`
 *   - `tunes`: Optional array of order-specific overrides for each order in the batch
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 * @throws {Error} If the total l1Amount is less than the total minL1Amount (thrown by constructCreateOrdersCall)
 *
 * @example
 * ```typescript
 * // Create a single order (common case)
 * const createSingleOrderCommand = createOrdersUiCommand({
 *   contractId: '0x...',
 *   orders: [
 *     [
 *       1,
 *       {
 *         baseAmount: 1000000000000000000n, // 1 CWEB
 *         l1Amount: 50000000n, // 0.5 BTC
 *         minL1Amount: 40000000n,
 *         baseRecipient: {
 *           auth: 'EcdsaContract',
 *           payload: 'k:user-address'
 *         }
 *       }
 *     ]
 *   ],
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Create multiple orders with variations
 * const createMultipleOrdersCommand = createOrdersUiCommand({
 *   contractId: '0x...',
 *   orders: [
 *     [
 *       3, // Create 3 orders
 *       {
 *         baseAmount: 1000000000000000000n, // Base: 1 CWEB
 *         l1Amount: 50000000n, // Base: 0.5 BTC
 *         baseRecipient: { auth: 'EcdsaContract', payload: 'k:user-address' }
 *       },
 *       [
 *         { baseAmount: 1100000000000000000n }, // Order 1: 1.1 CWEB
 *         { l1Amount: 60000000n }, // Order 2: 0.6 BTC
 *         { baseAmount: 1200000000000000000n, l1Amount: 70000000n } // Order 3
 *       ]
 *     ]
 *   ]
 * });
 *
 * // Use in QRCode component
 * <QRCode value={createMultipleOrdersCommand} />
 * ```
 */
export const createOrdersUiCommand = (data) => {
    return constructUiCommand([constructCreateOrdersCall(data)]);
};
/**
 * Creates a UI command for canceling a single order.
 * This is a convenience function that wraps `cancelOrdersUiCommand` for the common case of
 * canceling a single order. It generates a QR code-ready command string.
 *
 * Used in: CancelPositionModal
 *
 * @param data - Cancel order request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.orderId - The ID of the order to cancel
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Cancel a single order
 * const cancelOrderCommand = cancelOrderUiCommand({
 *   contractId: '0x...',
 *   orderId: 'order-123',
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={cancelOrderCommand} />
 * ```
 */
export const cancelOrderUiCommand = ({ contractId, orderId, accessId }) => {
    return constructUiCommand([
        constructCancelOrdersCall({
            contractId,
            orderIds: [orderId],
            accessId,
        }),
    ]);
};
/**
 * Creates a UI command for canceling a batch of one or more orders.
 * It supports canceling multiple orders in a single transaction, which is more efficient
 * than canceling them individually.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the cancel orders transaction.
 *
 * @param data - Cancel orders request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.orderIds - Array of order IDs to cancel
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Cancel a single order
 * const cancelSingleOrderCommand = cancelOrdersUiCommand({
 *   contractId: '0x...',
 *   orderIds: ['order-123']
 * });
 *
 * // Cancel multiple orders
 * const cancelMultipleOrdersCommand = cancelOrdersUiCommand({
 *   contractId: '0x...',
 *   orderIds: ['order-1', 'order-2', 'order-3'],
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={cancelMultipleOrdersCommand} />
 * ```
 */
export const cancelOrdersUiCommand = (data) => {
    return constructUiCommand([constructCancelOrdersCall(data)]);
};
/**
 * Creates a UI command for requesting execution of a PACT swap.
 * It generates a QR code-ready command string that can be scanned by a Coinweb wallet
 * to execute the swap transaction.
 *
 * @param data - PACT request parameters
 * @param data.contractId - The L2 contract address of the market maker contract
 * @param data.cwebAmount - The amount of CWEB to send as part of the swap (in smallest unit, e.g., wei)
 * @param data.requestData - Transfer call arguments containing the swap execution details
 *   (typically includes recipient addresses, amounts, and other execution parameters)
 * @param data.accessId - Optional unique identifier for transaction tracking/history access
 * @returns A UI command string that can be encoded as a QR code for wallet scanning
 *
 * @example
 * ```typescript
 * // Request execution of a swap
 * const swapCommand = constructRequestPactUiCommand({
 *   contractId: '0x...',
 *   cwebAmount: 1000000000000000000n, // 1 CWEB
 *   requestData: {
 *     // Transfer call arguments for the swap
 *   },
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={swapCommand} />
 *
 * // In a React hook
 * const generateSwapCommand = useCallback(
 *   (cwebAmount: bigint, requestData: RequestExecutionCallArguments, accessId?: string) =>
 *     constructRequestPactUiCommand({
 *       contractId: '0x...',
 *       cwebAmount,
 *       requestData,
 *       accessId
 *     }),
 *   []
 * );
 * ```
 */
export const constructRequestPactUiCommand = ({ contractId, accessId, cwebAmount, requestData }) => {
    return constructUiCommand([
        constructCall(contractId, PUBLIC_METHODS.REQUEST_EXECUTION, withMetadata(requestData, accessId), FEE.HANDLE_EXECUTION_REQUEST + cwebAmount + (accessId ? HISTORY_ACCESS_FEE : 0n)),
    ]);
};
